import React from 'react';
import { useTranslation } from 'react-i18next';

import { store } from '@/app/store';

import {
  DEVICE_INFO_REFRESH_HOURS_DEFAULT,
  DEVICE_INFO_VERSION_DEFAULT,
  DeviceInfoSnapshot,
  MasterInfo,
} from '@/features/master/masterSlice';

import Button from '@/components/Common/Button';
import FlexRow from '@/components/Common/FlexRow';

import {
  GroupDivider,
  InfoBanner,
  Pill,
  SettingsInput,
  su,
} from '@/pages/Settings/settingsUi';

export interface SkywayEdit {
  device_info_api_url:		string;
  device_info_api_key:		string;
  device_info_api_secret:	string;
  device_info_version:		string;
  device_info_refresh_hours:	number;
}

export function toSkywayEdit(master: MasterInfo): SkywayEdit {
  return {
    device_info_api_url:		master.device_info_api_url || "",
    device_info_api_key:		master.device_info_api_key || "",
    device_info_api_secret:		"",
    device_info_version:		master.device_info_version || DEVICE_INFO_VERSION_DEFAULT,
    device_info_refresh_hours:	master.device_info_refresh_hours || DEVICE_INFO_REFRESH_HOURS_DEFAULT,
  };
}

export function skywayApiUrlInvalid(sky: SkywayEdit): boolean {
  const url = (sky.device_info_api_url || '').trim();

  if (url === '')
    return false;	// blank is legal — disk-only mode

  return !/^https?:\/\/[a-zA-Z0-9.\-:]+(\/.*)?$/.test(url);
}

export function skywayRefreshHoursInvalid(sky: SkywayEdit): boolean {
  const h = sky.device_info_refresh_hours;

  return !Number.isInteger(h) || h < 1 || h > 8760;
}

export function skywayInvalid(sky: SkywayEdit): boolean {
  return skywayApiUrlInvalid(sky) || skywayRefreshHoursInvalid(sky);
}

// The secret is write-only: an empty string tells the backend to keep whatever
// it already stores, so an untouched form re-saves the rest without ever
// round-tripping the secret. `device_info_api_secret_set` is read-only and is
// never posted back.
export function buildSkywayPayload(sky: SkywayEdit): Partial<MasterInfo> {
  return {
    device_info_api_url:		(sky.device_info_api_url || '').trim(),
    device_info_api_key:		(sky.device_info_api_key || '').trim(),
    device_info_api_secret:		sky.device_info_api_secret || "",
    device_info_version:		(sky.device_info_version || '').trim() || DEVICE_INFO_VERSION_DEFAULT,
    device_info_refresh_hours:	sky.device_info_refresh_hours,
  };
}

// "api" is the healthy state; "file" means the vendor API was unreachable and
// we are serving the on-disk seed, "empty" that no load has completed yet.
const sourceTone = (source: string) => (source === 'api' ? 'ok' : 'warn');

export interface SkywayProps {
  skyway:			SkywayEdit;
  setSkyway:		(sky: SkywayEdit) => any;
  secretSet:		boolean;
  triedSave:		boolean;
  isLoaded:			boolean;
  canRefresh:		boolean;
}

const Skyway = (props: SkywayProps) => {
  const { t } = useTranslation();

  const sky = props.skyway;

  // The secret input stays hidden behind a "Change" button while a secret is
  // stored, so a normal edit cannot blank it by accident.
  const [changingSecret, setChangingSecret] = React.useState(!props.secretSet);
  const [refreshing, setRefreshing] = React.useState(false);
  const [snapshot, setSnapshot] = React.useState(null as DeviceInfoSnapshot | null);
  const [hidden, setHidden] = React.useState(false);

  const retryRef = React.useRef(0 as any);

  React.useEffect(() => {
    setChangingSecret(!props.secretSet);
  }, [props.secretSet]);

  React.useEffect(() => {
    return () => {
      if (retryRef.current)
        clearTimeout(retryRef.current);
    };
  }, []);

  const update = (patch: Partial<SkywayEdit>) => {
    props.setSkyway({ ...sky, ...patch });
  };

  const runRefresh = React.useCallback(async (isRetry?: boolean) => {
    setRefreshing(true);
    try {
      const res = await store.skywayRefresh();

      if (res.ok) {
        setSnapshot(res.snapshot);
        return;
      }

      // 403 — the caller lacks RefreshDeviceInfo; they should not have seen
      // the button at all, so take it away.
      if (res.status === 403) {
        setHidden(true);
        return;
      }

      // 503 — the device-info manager is still starting up. One retry after a
      // second, per the spec; apiOperation has already surfaced the message.
      if (res.status === 503 && !isRetry) {
        retryRef.current = setTimeout(() => runRefresh(true), 1000);
      }
    } finally {
      setRefreshing(false);
    }
  }, []);

  const secretField = changingSecret
    ?	<SettingsInput label={t('system:skyway:secret:caption')}
                       type="password"
                       placeholder={t('system:skyway:secret:placeholder')}
                       value={sky.device_info_api_secret}
                       hint={props.secretSet
                               ? t('system:skyway:secret:hint_set')
                               : t('system:skyway:secret:hint_unset')}
                       isLoaded={props.isLoaded}
                       setValue={(v) => update({ device_info_api_secret: v })} />
    :	<FlexRow direction="column" align="start" className="gap-[6px] w-full">
          <FlexRow className="gap-[12px] w-full">
            <FlexRow flex={1} className="gap-[8px]">
              <span className="text-[13px] text-[#6B7280]">
                { t('system:skyway:secret:caption') }
              </span>
              <span className="text-[15px] tracking-[2px] text-[#1F2733]">
                ●●●●●●
              </span>
            </FlexRow>
            <FlexRow flex={0}>
              <span className="text-[13px] font-medium text-[--Primetime-main] cursor-pointer select-none"
                    onClick={() => setChangingSecret(true)}>
                { t('system:skyway:secret:change') }
              </span>
            </FlexRow>
          </FlexRow>
          <span className={su.hint}>{ t('system:skyway:secret:hint_set') }</span>
        </FlexRow>;

  return (
  <React.Fragment>
    <FlexRow align="start" className="gap-[16px] w-full">
      <SettingsInput label={t('system:skyway:api_url:caption')}
                     placeholder="https://dms.skyway.example.com"
                     value={sky.device_info_api_url}
                     invalid={props.triedSave && skywayApiUrlInvalid(sky)}
                     error={t('system:skyway:api_url:invalid')}
                     hint={t('system:skyway:api_url:hint')}
                     isLoaded={props.isLoaded}
                     setValue={(v) => update({ device_info_api_url: v })} />

      <SettingsInput label={t('system:skyway:api_key:caption')}
                     value={sky.device_info_api_key}
                     isLoaded={props.isLoaded}
                     setValue={(v) => update({ device_info_api_key: v })} />
    </FlexRow>

    { secretField }

    <FlexRow align="start" className="gap-[16px] w-full">
      <SettingsInput label={t('system:skyway:version:caption')}
                     placeholder={DEVICE_INFO_VERSION_DEFAULT}
                     value={sky.device_info_version}
                     isLoaded={props.isLoaded}
                     setValue={(v) => update({ device_info_version: v })} />

      <SettingsInput label={t('system:skyway:refresh_hours:caption')}
                     placeholder={String(DEVICE_INFO_REFRESH_HOURS_DEFAULT)}
                     value={String(sky.device_info_refresh_hours)}
                     invalid={props.triedSave && skywayRefreshHoursInvalid(sky)}
                     error={t('system:skyway:refresh_hours:invalid')}
                     hint={t('system:skyway:refresh_hours:hint')}
                     isLoaded={props.isLoaded}
                     setValue={(v) => {
                       const trimmed = v.trim();
                       if (trimmed === '') {
                         update({ device_info_refresh_hours: 0 });
                         return;
                       }
                       const n = parseInt(trimmed, 10);
                       if (!isNaN(n))
                         update({ device_info_refresh_hours: n });
                     }} />
    </FlexRow>

    { !props.canRefresh || hidden
        ?	null
        :	<React.Fragment>
              <GroupDivider caption={t('system:skyway:inventory:caption')} />

              <FlexRow align="start" className="gap-[16px] w-full">
                <FlexRow direction="column" align="start" flex={1} className="gap-[4px]">
                  <span className="text-[14px] font-medium text-[#1F2733]">
                    { t('system:skyway:refresh:caption') }
                  </span>
                  <span className={su.subtitle}>
                    { t('system:skyway:refresh:hint') }
                  </span>
                </FlexRow>
                <FlexRow flex={0}>
                  <Button caption={t('system:skyway:refresh:button')}
                          icon="sync"
                          layout="white"
                          isDisabled={refreshing}
                          onClick={() => runRefresh()} />
                </FlexRow>
              </FlexRow>

              { snapshot &&
                <FlexRow direction="column" align="start" className="gap-[10px] w-full">
                  <FlexRow className="gap-[8px]">
                    <Pill tone="ok" label={t('system:skyway:snapshot:queued')} icon="check" />
                    <Pill tone={sourceTone(snapshot.source)}
                          label={t('system:skyway:snapshot:source', { source: snapshot.source })} />
                    <Pill tone="info"
                          label={t('system:skyway:snapshot:total', { total: snapshot.total })} />
                  </FlexRow>

                  <span className={su.hint}>
                    { snapshot.last_update
                        ? t('system:skyway:snapshot:last_update', { date: snapshot.last_update })
                        : t('system:skyway:snapshot:never') }
                  </span>

                  {/* source "file"/"empty" means the vendor API was unreachable —
                      the numbers above are stale, so say so rather than let them
                      read as a healthy result. */}
                  { snapshot.source !== 'api' &&
                    <InfoBanner tone="warn"
                                text={snapshot.error
                                        ? t('system:skyway:snapshot:degraded_error', { error: snapshot.error })
                                        : t('system:skyway:snapshot:degraded')} />
                  }

                  <span className={su.hint}>
                    { t('system:skyway:snapshot:pre_reload') }
                  </span>
                </FlexRow>
              }
            </React.Fragment>
    }
  </React.Fragment>
  );
};

export default Skyway;
